/**
 * Global layout styles for `<wm-tab-manager>`. The control renders in the light
 * DOM (ViewBase), so its layout rules must live at document level; injecting
 * them from here makes the control self-styling - host apps need no custom
 * stylesheet entries. Kept intentionally minimal: the tab visuals belong to the
 * webmodule-tab-group component; this only arranges the group, the footer
 * button bar, and the nav-slot action area. Do not add deep DOM-shape selectors
 * here (see the legacy .page-control chains in dealer_ui.scss for what to avoid).
 */
const tabManagerCss = `
  wm-tab-manager {
    /* !important matches how .page-control-container wins against the legacy
     * #main-body height chain (dealer_ui.scss), which forces display:block on
     * page children with an id-weighted selector this rule cannot out-rank. */
    display: flex !important;
    flex-direction: column;
    min-height: 0;
  }

  wm-tab-manager > webmodule-tab-group {
    flex: 1 1 auto;
    min-height: 0;
  }

  /* Pages whose button bar is empty render no footer element at all, which
   * would leave the tab body flush against whatever follows (the site
   * footer). The group is the manager's last child exactly in that case, so
   * give it the same breathing room the footer bar's padding provides. */
  wm-tab-manager > webmodule-tab-group:last-child {
    margin-block-end: var(--webmodule-spacing-medium, 1rem);
  }

  wm-tab-manager > .tab-manager__footer {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    /* right-anchor the primary + overall regions; a present secondary region
     * pulls itself to the start side via its auto margin */
    justify-content: flex-end;
    gap: 0.25rem;
    /* breathing room around the action row, matching the legacy
     * .page-nav-buttons spacing on the quote pages (~1rem at desktop) */
    padding: var(--webmodule-spacing-medium, 1rem) 0;
  }

  wm-tab-manager .tab-manager__nav-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-inline-start: auto;
    padding-inline: 0.25rem;
  }

  /* buttonBar 'nav': the tab strip normally hugs its tabs, which would leave
   * the action area glued to the last tab. Stretch the strip across the rail
   * so the actions' auto margin pushes them to the end, like the legacy
   * in-strip buttons. The manager toggles the class while nav buttons exist. */
  wm-tab-manager.tab-manager--nav-buttons > webmodule-tab-group::part(tabs) {
    flex: 1 1 auto;
  }

  /* The three button bar regions: page secondary (start side), page primary
   * (end side) and the always-rendered overall region (far end). */
  wm-tab-manager .tab-manager__buttons-secondary,
  wm-tab-manager .tab-manager__buttons-primary,
  wm-tab-manager .tab-manager__buttons-overall {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  wm-tab-manager .tab-manager__buttons-secondary {
    margin-inline-end: auto;
  }

  /* TabAction.heading: a non-clickable caption above a dropdown's items,
   * inset to line up with the menu item text. */
  wm-tab-manager .tab-manager__menu-heading {
    display: block;
    color: var(--webmodule-color-neutral-500);
    padding: var(--webmodule-spacing-2x-small) var(--webmodule-spacing-x-large);
    user-select: none;
  }

  /* pages flagged tabHidden keep their panel but hide the tab; cannot use the
   * tab's disabled attribute for this as the group refuses to activate disabled tabs */
  wm-tab-manager webmodule-tab[data-tab-hidden] {
    display: none;
  }

  /* Fill-height tables (DataTableWrapper.fillHeight() adds the wm-table-fill-host
   * marker): give their panel the full bounded height of the tab body so the
   * wrapper's height:100% chain resolves and the paginator pins to the panel
   * bottom instead of floating under the rows. Successor to the legacy
   * .page-control-tab-content > [role='tabpanel']:has(...) rule in dealer_ui.scss;
   * panels without a fill-host table stay content-height. */
  wm-tab-manager webmodule-tab-panel:has(.wm-table-fill-host) {
    height: 100%;
  }

  /* Some tabs wrap the fill-host in an extra view element (e.g. the supplier
   * support inbox tabs) instead of making the DataTableWrapper the direct
   * panel content. That wrapper must pass the bounded panel height down to
   * the fill-host, or the table hugs its rows and dead space opens between
   * the paginator and the panel bottom. Twin of the legacy
   * .page-control-tab-content wrapper rule in dealer_ui.scss; the :not()
   * keeps it inert when the fill-host is the direct panel child. */
  wm-tab-manager webmodule-tab-panel:has(.wm-table-fill-host) > *:not(.wm-table-fill-host) {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
  }

  /* The fill-host's own height:100% does not resolve through that extra flex
   * wrapper, so drive it with flex-grow instead. */
  wm-tab-manager webmodule-tab-panel:has(.wm-table-fill-host) > *:not(.wm-table-fill-host) > .wm-table-fill-host {
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
  }

  /* hideSingleTab: the manager toggles this class while one or zero tabs are visible */
  wm-tab-manager.tab-manager--hide-nav > webmodule-tab-group::part(nav) {
    display: none;
  }
`;

let injected = false;

/**
 * Adds the tab-manager layout styles to the document once. Adopted sheets sort
 * after all document stylesheets, so these rules win specificity ties against
 * host app css exactly like the old site-stylesheet copy did.
 */
export function injectTabManagerStyles() {
  if (injected) return;
  injected = true;
  // test the prototype, not `document`: lib.dom types the property as always
  // present, so an `in document` check would narrow the else branch to never
  if ('adoptedStyleSheets' in Document.prototype) {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(tabManagerCss);
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  } else {
    const style = document.createElement('style');
    style.textContent = tabManagerCss;
    document.head.appendChild(style);
  }
}
